import { useEffect, useImperativeHandle, useState } from 'react'
import {
  DropDownList,
  DropDownListChangeEvent
} from '@progress/kendo-react-dropdowns'
import { TextArea, TextAreaChangeEvent } from '@progress/kendo-react-inputs'
import { useAppSelector } from '../../stores'
import { Authority } from '../../gateway/system/Authority'
import { CodeName } from '../../services/initializing/CodeName'
import { GetMessageGateway } from '../../gateway/home/GetMessageGateway'
import { HomeMsg } from '@gateway.Home'
import { withRefFC } from '../../components/hooks/withRefFC'
import { useLeaveGuard } from '../../components/hooks'
import './HomeInfomation.css'
import { ErrorMessage, ErrorMessages } from '../../components/parts'

export interface HomeInformationHandler {
  // 画面で更新されたデータを取得する
  getUpdatedMsg: () => HomeMsg | undefined
  fetchAllMessages: () => void
}
export const HomeInformation = withRefFC<HomeInformationHandler>((ref) => {
  // お知らせドロップダウンで選択した値
  const [noticeValue, setNoticeValue] = useState<CodeName>()
  // 支店で選択した値
  const [branchValue, setBranchValue] = useState<CodeName>()
  const [text, setText] = useState('')
  // 更新中のmsg
  const [updatingMsg, setUpdatingMsg] = useState<HomeMsg | undefined>(undefined)
  // 全メッセージ
  const [msgList, setMsgList] = useState<HomeMsg[]>([])
  // 画面．支店コード表示
  const [showBranchBox, setShowBranchBox] = useState(true)

  const [errorMessages, setErrorMessages] = useState<ErrorMessage[]>([])

  useLeaveGuard([text], '内容が編集中です。このページを離れますか？')

  const common = useAppSelector((x) => x.common)
  const codes = common.initialData.codeNames('TE01')
  // 支店情報のリストを取得
  const branches = common.initialData.codeNames('SI01')

  const userProp = useAppSelector((x) => x.common.userProp)

  useImperativeHandle(ref, () => ({
    getUpdatedMsg: () => {
      if (updatingMsg !== undefined && text !== updatingMsg.txt) {
        // msg 更新
        return {
          ...updatingMsg,
          txt: text,
          updDtm: updatingMsg.updDtm
        }
      }

      if (updatingMsg === undefined && text !== '') {
        // msg 新規作成
        const txtKbn =
          codes.find((code) => code.CODE === noticeValue?.CODE)?.TXT1 ?? ''
        const newMsg: HomeMsg = {
          txtId: '',
          txtKbn,
          branchNm: branchValue?.NAME,
          txt: text,
          updDtm: new Date().toISOString()
        }
        return newMsg
      }

      // 値の追加/変更がない場合
      return undefined
    },
    fetchAllMessages
  }))

  /**
   * お知らせ選択の変更
   */
  const handleSelectNotice = (event: DropDownListChangeEvent) => {
    setNoticeValue(event.value)
    // 社内以外の場合、画面．支店コード表示無し
    setShowBranchBox(!(event.value.CODE !== '01'))
    if (event.value.CODE !== '01') {
      // 支店コード空にする
      setBranchValue(undefined)
    } else {
      setBranchValue(branches[0])
    }
  }

  /**
   * テキストの変更
   */
  const handleChangeTextArea = (event: TextAreaChangeEvent) => {
    setText(event.value)
  }

  /**
   * 支店を選択の変更
   */
  const handleSelectBranch = (event: DropDownListChangeEvent) => {
    setBranchValue(event.value)
  }

  /**
   * お知らせのドロップダウンリストを取得
   */
  const getNoticeListData = () => {
    let noticeListData: CodeName[] = []
    if (userProp?.authority === Authority.Branch) {
      // 支店担当者の場合、自支店の社内お知らせのみ更新可能
      noticeListData = codes.filter((code) => code.CODE === '01')
    } else {
      noticeListData = codes
    }
    return noticeListData
  }

  /**
   * 支店のドロップダウンリストを取得
   */
  const getBranchListData = () => {
    let branchListData: CodeName[] = []
    if (userProp?.authority === Authority.Branch) {
      // 支店担当者の場合、自支店の社内お知らせのみ更新可能
      branchListData = branches.filter((branch) => branch.CODE === userProp?.branchCode)
    } else {
      branchListData = branches
    }
    return branchListData
  }

  const fetchAllMessages = () => {
    GetMessageGateway.getAllMessages()
      .then((result) => {
        if (!result.error) {
          setMsgList(result.data.msgList)
        } else {
          if (Array.isArray(result.messages) && result.messages.length > 0) {
            const errorMessages = result.messages.map<ErrorMessage>((message) => {
              return {
                id: message.id,
                content: message.message
              }
            })
            setErrorMessages(errorMessages)
          }
        }
      })
      .finally(() => {})
  }

  /**
   * 画面初期表示 ---全メッセージ取得
   */
  useEffect(() => {
    fetchAllMessages()
  }, [])

  /**
   *  初期値の設定
   */
  useEffect(() => {
    setNoticeValue(codes[0])
    const initialBranch = userProp?.authority === Authority.Branch
      ? branches.find((branch) => branch.CODE === userProp?.branchCode)
      : branches[0]
    setBranchValue(initialBranch)
  }, [])

  /**
   * お知らせや支店選択が変化した場合、テキストを設定
   */
  useEffect(() => {
    let updatingMsg

    const txtKbn = codes.find((code) => code.CODE === noticeValue?.CODE)?.TXT1
    if (noticeValue?.CODE !== '01') {
      // 業者全体/費目別の場合
      updatingMsg = msgList.find((msg) => msg.txtKbn === txtKbn)
    } else {
      // 大成社内の場合
      updatingMsg = msgList.find(
        (msg) => msg.txtKbn === txtKbn && msg.txtId === branchValue?.CODE
      )
    }

    setText(updatingMsg?.txt ?? '')
    setUpdatingMsg(updatingMsg)
  }, [noticeValue, branchValue, msgList])

  return (
    <>
      <DropDownList
        style={{ marginBottom: 10, width: '50%' }}
        onChange={handleSelectNotice}
        data={getNoticeListData()}
        textField="NAME"
        dataItemKey="CODE"
        value={noticeValue}
        popupSettings={{ className: 'pcy-dropdownlist' }}
      />
      {showBranchBox && (
        <DropDownList
          style={{ marginBottom: 10, marginLeft: 30, width: '30%' }}
          onChange={handleSelectBranch}
          data={getBranchListData()}
          textField="NAME"
          dataItemKey="CODE"
          value={branchValue}
          popupSettings={{ className: 'pcy-dropdownlist' }}
        />
      )}
      <TextArea rows={4} onChange={handleChangeTextArea} value={text} />
      <ErrorMessages type="dialog" messages={errorMessages} />
    </>
  )
})
